import { createBrowserRouter } from 'react-router-dom'
import { lazy, Suspense } from 'react'

import AppShellLayout from '@/app/layouts/AppShellLayout'
import TableLayout from './app/layouts/TableLayout'
import PrivateLayout from './app/layouts/PrivateLayout'
import PublicLayout from './app/layouts/PublicLayout'
import RouteErrorBoundary from './features/navigation/components/RouteErrorBoundary'
import RequireModule from '@features/modules/components/RequireModule'
import FirstAvailablePageRedirect from '@features/modules/components/FirstAvailablePageRedirect'
import Loading from '@features/shared/components/Loading'

import {
  assistantRoutes,
  homeRoutes,
  preloadPageAt,
  automationsRoutes,
  integrationsRoutes,
  tableRoutes,
  settingsRoutes,
  usersRoutes,
} from './routes/privateRoutes'
import { publicRoutes } from './routes/publicRoutes'

const NotFoundPage = lazy(() => import('./app/pages/NotFoundPage'))

preloadPageAt(window.location.pathname)

export const router = createBrowserRouter([
  {
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        // The signed-out pages: never offered by the route picker.
        element: <PublicLayout />,
        handle: { routePicker: { public: true } },
        children: publicRoutes,
      },
      {
        // One shell for every signed-in page, so moving between table pages
        // and the rest keeps the header, rail, and side panels mounted.
        element: <AppShellLayout />,
        children: [
          {
            element: <TableLayout />,
            children: tableRoutes,
          },
          {
            element: <PrivateLayout />,
            children: [
              // `/` forwards to the Home Page, or to the AI Assistant at /ai; with AI
              // switched off, /ai sends the user to the first page in the rail
              // instead of showing Not Found.
              ...homeRoutes,
              {
                element: (
                  <RequireModule moduleSlug="ai" fallback={<FirstAvailablePageRedirect />} />
                ),
                children: assistantRoutes,
              },
              ...settingsRoutes,
              ...usersRoutes,
              // The guard sits above the lazy pages so a tenant without the
              // Automations module never loads its chunks and sees Not Found.
              { element: <RequireModule moduleSlug="automations" />, children: automationsRoutes },
              {
                element: <RequireModule moduleSlug="integrations" />,
                children: integrationsRoutes,
              },
            ],
          },
        ],
      },
      {
        path: '*',
        // Outside the layouts' Suspense, so the lazy page brings its own.
        element: (
          <Suspense fallback={<Loading />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
])
